import React from 'react';
// @mui
import { LoadingButton } from '@mui/lab';
import { Box, Container, Grid, Typography } from '@mui/material';
// components
import Page from 'src/components/Page';
import PageHeader from 'src/components/PageHeader';
import ReloadButton from 'src/components/ReloadButton';
import ReportTimeframeInput, {
  TimeframeDateRange,
  timeframes,
  timeframeToDateRange,
} from 'src/components/ReportTimeframeInput';
// hooks
import useVoicemailList, { Voicemail } from 'src/hooks/core-api/useVoicemailList';
import useSettings from 'src/hooks/useSettings';
//sections
import VoicemailList from 'src/sections/voicemail/VoicemailList';
// type
import { DateRange } from 'src/types';
//
import { captureException } from 'logrocket';
import useMainStore from 'src/store';

const VoicemailSettingsPage = () => {
  const pageTitle = 'Voicemail';

  const { themeStretch } = useSettings();

  const defaultTimeframe = timeframes[0];
  const defaultDateRange = timeframeToDateRange(defaultTimeframe);

  const [dateRange, setDateRange] = React.useState<DateRange>(defaultDateRange);

  const selectedLocations = useMainStore((state) => state.selectedLocations);

  const { fetchNextPage, hasNextPage, data, isFetching, isLoading, isError, error, refetch } =
    useVoicemailList({
      request: {
        locationIds: selectedLocations?.map((x) => x.id),
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        limit: 50,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });

  const firstPage = data?.pages?.[0];
  const firstPageHasData = firstPage && firstPage.items.length > 0;

  const handleTimeframeDateRangeChange = (tdr: TimeframeDateRange) => {
    console.log('Timeframe and Date range changed', tdr);
    setDateRange(tdr.dateRange);
  };

  if (isError) {
    captureException(error as Error);
    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    return (
      <>
        <Typography variant="h3">Error loading call history</Typography>
        {errorMessage && <Typography variant="subtitle1">{errorMessage}</Typography>}
      </>
    );
  }

  return (
    <Page title={pageTitle}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <PageHeader
              title={pageTitle}
              button={
                <ReloadButton
                  tooltip="Refresh report"
                  isFetching={isLoading || isFetching}
                  onRefetch={refetch}
                />
              }
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <ReportTimeframeInput
              onChange={handleTimeframeDateRangeChange}
              defaultTimeframe={defaultTimeframe}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            {isLoading && (
              <Typography variant="h5" sx={{ textAlign: 'center' }}>
                Loading...
              </Typography>
            )}
            {!firstPageHasData && !isLoading && (
              <Typography variant="h5" sx={{ textAlign: 'center' }}>
                No results to display
              </Typography>
            )}
            {data?.pages.map((group, i) => {
              if (group) {
                return (
                  <React.Fragment key={i}>
                    <Box sx={{ ml: 1.5 }}>
                      <VoicemailList
                        voicemailListGroup={group.items}
                        previousVoicemailListGroup={data.pages[i - 1]?.items ?? ([] as Voicemail[])}
                      />
                    </Box>
                  </React.Fragment>
                );
              }
              return null;
            })}
            {firstPageHasData && (
              <LoadingButton
                sx={{ ml: 0.5 }}
                onClick={() => fetchNextPage()}
                size="large"
                loading={isFetching}
                disabled={!hasNextPage}
              >
                More voicemails
              </LoadingButton>
            )}
          </Grid>
          <Grid item md={3} lg={2} xl={1} />
          <Grid
            item
            flexGrow={1}
            sx={{
              mt: 4,
              display: {
                xs: 'none',
                md: 'block',
              },
            }}
          />
          <Grid item xs={1} />
        </Grid>
      </Container>
    </Page>
  );
};

export default VoicemailSettingsPage;
